import { connectWebSocket, disconnectWebSocket, on } from './serverClient';
import { getFriendList } from './friendsApi';
import { getUnreadCount } from './arena/mailApi';
import { setActivityLabel } from './arena/profileApi';
import { receive as receiveMoment } from './moments/momentsManager';

// Friend metadata (avatar, name, rank…) is only refreshed by hitting `/friends`.
// 30s gives sub-minute eventual consistency for things like avatar swaps without
// being noisy. The focus + sidebar-open triggers handle "right now" cases.
const FRIEND_REFRESH_INTERVAL_MS = 30_000;

let currentActivity = 'hub';
let currentActivityData = null;
let onFriendListUpdate = null;
let onNewNotifications = null;
let onMailCountUpdate = null;
let onChatMessage = null;
let onChatClaimed = null;
let onSessionActive = null;
let onDraftActive = null;
let onProfileUpdate = null;
let onSeasonRefreshNeeded = null;

let friendList = { friends: [], pendingRequests: [], pendingCount: 0 };
let unsubscribers = [];
let lastMailCount = 0;
let friendRefreshTimer = null;
let visibilityHandler = null;
let focusHandler = null;

function emitFriendList() {
  if (onFriendListUpdate) onFriendListUpdate(friendList);
}

async function refreshFriendListInternal() {
  try {
    const data = await getFriendList();
    if (!data) return;
    // Preserve online/presence info from the existing list when the REST
    // endpoint doesn't include it (e.g. an older server). For a
    // well-formed response the REST payload IS the source of truth.
    // WebSocket presence:update events then overlay any change since the
    // fetch started.
    const prevMap = new Map((friendList.friends || []).map((f) => [f.id, f]));
    friendList = {
      ...data,
      friends: (data.friends || []).map((f) => {
        const prev = prevMap.get(f.id) || {};
        return {
          ...f,
          online: f.online ?? prev.online ?? false,
          presenceStatus: f.presenceStatus ?? prev.presenceStatus ?? null,
          customStatus: f.customStatus ?? prev.customStatus ?? null,
        };
      }),
    };
    emitFriendList();
  } catch (err) {
    console.error('[presence] refreshFriendList failed:', err);
  }
}

/** Public refresh. Used by the app when the user opens the friends sidebar. */
export function refreshFriendList() {
  return refreshFriendListInternal();
}

/** Last fetched friend list. Lets surfaces that only know a player id
 *  (notification rows) borrow the avatar without another round trip. */
export function getCachedFriendList() {
  return friendList;
}

async function refreshMailCount() {
  try {
    const data = await getUnreadCount();
    if (onMailCountUpdate) onMailCountUpdate(data);
    lastMailCount = data?.count || 0;
    return data;
  } catch (err) {
    console.error('[presence] refreshMailCount failed:', err);
    return null;
  }
}

/** Manually trigger a mail count refresh (used by the Mailbox after claim/delete). */
export function refreshMailbox() {
  return refreshMailCount();
}

export async function startPresence(activity, callbacks = {}) {
  onFriendListUpdate = callbacks.onFriendListUpdate || null;
  onNewNotifications = callbacks.onNewNotifications || null;
  onMailCountUpdate = callbacks.onMailCountUpdate || null;
  onChatMessage = callbacks.onChatMessage || null;
  onChatClaimed = callbacks.onChatClaimed || null;
  onSessionActive = callbacks.onSessionActive || null;
  onDraftActive = callbacks.onDraftActive || null;
  onProfileUpdate = callbacks.onProfileUpdate || null;
  onSeasonRefreshNeeded = callbacks.onSeasonRefreshNeeded || null;
  currentActivity = activity;

  await connectWebSocket();

  unsubscribers.push(on('presence:snapshot', (data) => {
    if (!data?.friends) return;
    const byId = new Map(data.friends.map((f) => [f.playerId, f]));
    friendList = {
      ...friendList,
      friends: (friendList.friends || []).map((f) => {
        const snap = byId.get(f.id);
        if (!snap) return f;
        return {
          ...f,
          online: snap.online ?? false,
          presenceStatus: snap.presenceStatus ?? null,
          customStatus: snap.customStatus ?? null,
        };
      }),
    };
    emitFriendList();
  }));

  unsubscribers.push(on('presence:update', (data) => {
    if (!data?.playerId) return;
    friendList = {
      ...friendList,
      friends: (friendList.friends || []).map((f) =>
        f.id === data.playerId
          ? {
              ...f,
              online: !!data.online,
              presenceStatus: data.presenceStatus ?? null,
              customStatus: data.customStatus ?? null,
            }
          : f
      ),
    };
    emitFriendList();
  }));

  unsubscribers.push(on('friend:request', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'friend-request',
        senderId: data.senderId,
        senderName: data.senderName,
        senderAvatar: data.senderAvatar,
      }]);
    }
    refreshFriendListInternal();
  }));

  unsubscribers.push(on('friend:accepted', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'friend-accepted',
        name: data.name,
        avatar: data.avatar,
      }]);
    }
    refreshFriendListInternal();
  }));

  unsubscribers.push(on('friend:removed', () => {
    refreshFriendListInternal();
  }));

  unsubscribers.push(on('mail:received', () => {
    refreshMailCount().then((data) => {
      if (!data) return;
      if (onNewNotifications) {
        onNewNotifications([{
          type: 'new-mail',
          count: data.count,
          newCount: 1,
        }]);
      }
    });
  }));

  unsubscribers.push(on('draft:player_disconnected', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'draft-player-disconnected',
        playerName: data.playerName,
        eventId: data.eventId,
        gracePeriodMs: data.gracePeriodMs,
      }]);
    }
  }));

  unsubscribers.push(on('draft:player_reconnected', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'draft-player-reconnected',
        playerName: data.playerName,
        eventId: data.eventId,
      }]);
    }
  }));

  unsubscribers.push(on('draft:cancelled', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'draft-aborted',
        eventId: data.eventId,
        reason: data.reason,
      }]);
    }
  }));

  unsubscribers.push(on('invite:received', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'match-invite',
        senderId: data.senderId,
        senderName: data.senderName,
      }]);
    }
  }));

  unsubscribers.push(on('invite:accepted', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'invite-accepted',
        roomCode: data.roomCode,
        isHost: data.isHost,
        automations: data.automations,
        opponent: data.opponent,
        name: data.opponent?.name,
      }]);
    }
  }));

  unsubscribers.push(on('invite:declined', () => {
    // No UI notification for declines yet. Could be added later.
  }));

  unsubscribers.push(on('invite:error', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'invite-error',
        error: data?.error || 'Invite failed',
      }]);
    }
  }));

  unsubscribers.push(on('matchmaking:matched', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{
        type: 'matchmaking-matched',
        roomCode: data.roomCode,
        isHost: data.isHost,
        mode: data.mode || 'ranked',
        opponent: data.opponent,
      }]);
    }
  }));

  unsubscribers.push(on('auction:sold', () => {
    // Auction proceeds arrive via mail. Mail:received will trigger
    // refresh. Also poke the host so it can refetch the season:
    // auction sales advance the seller's `sell_auction` quest, and
    // the per-quest counters live in /me/season's activeQuests view
    // which has to be re-fetched for the Trials grid to update.
    if (onSeasonRefreshNeeded) onSeasonRefreshNeeded('auction:sold');
  }));

  unsubscribers.push(on('chat:message', (data) => {
    if (onChatMessage) onChatMessage(data);
  }));

  // Moments (ephemeral social feed). Forward to the manager. It owns
  // the IndexedDB bucket + object-URL lifecycle.
  unsubscribers.push(on('moment:received', (data) => {
    receiveMoment(data).catch((err) => console.error('[moments] receive failed:', err));
  }));
  unsubscribers.push(on('moment:error', (data) => {
    if (onNewNotifications) {
      onNewNotifications([{ type: 'moment-error', error: data?.error || 'Failed to post moment' }]);
    }
  }));

  unsubscribers.push(on('chat:claimed', (data) => {
    if (onChatClaimed) onChatClaimed(data);
  }));

  unsubscribers.push(on('session:active', (data) => {
    if (onSessionActive) onSessionActive(data);
  }));

  // Draft analog of session:active. The server tells a (re)connecting client
  // which in-progress draft to auto-rejoin after an app restart.
  unsubscribers.push(on('draft:active', (data) => {
    if (onDraftActive) onDraftActive(data);
  }));

  // Server-authoritative profile snapshot. Fired after any mutation to
  // the current player's coins, shards, xp, rank, collection, season
  // progress, achievements, pending packs, etc. The handler replaces the
  // client's arenaProfile state so the UI reflects admin grants,
  // pack-claim rewards, trade flows, and anything else that previously
  // required a full reload.
  unsubscribers.push(on('profile:update', (data) => {
    if (onProfileUpdate) onProfileUpdate(data);
  }));

  await refreshFriendListInternal();
  await refreshMailCount();

  // Periodic background refresh. Catches avatar / name / rank changes that
  // friends made on their own clients. The WebSocket presence events only
  // cover online status, so without this poll, friend metadata would stay
  // frozen at whatever it was when the user logged in.
  friendRefreshTimer = setInterval(refreshFriendListInternal, FRIEND_REFRESH_INTERVAL_MS);

  // Snap to fresh data when the user comes back to the window. Far more
  // responsive than waiting up to 30s after they alt-tab back in.
  visibilityHandler = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      refreshFriendListInternal();
    }
  };
  focusHandler = () => refreshFriendListInternal();
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', visibilityHandler);
  if (typeof window !== 'undefined') window.addEventListener('focus', focusHandler);
}

// Map a (activity, data) pair to a friend-facing status label. Mirrors
// the seven Discord presence states from discordPresence.js so the
// in-app friend list and Discord show the same thing. When the user
// is in a match, both surfaces read "In a match vs Foo".
function deriveStatusLabel(activity, data) {
  switch (activity) {
    case 'hub':           return 'In the hub';
    case 'store':         return 'Browsing the store';
    case 'deckbuilder':   return data?.deckName ? `Editing ${data.deckName}` : 'In the deck builder';
    case 'deck-select':   return 'Choosing a deck';
    case 'matchmaking':   return 'Looking for a match';
    case 'in-match': {
      const ranked = data?.ranked ? 'Ranked match' : 'In a match';
      return data?.opponentName ? `${ranked} vs ${data.opponentName}` : ranked;
    }
    case 'auction-house': return 'At the auction house';
    case 'pack-opening':  return 'Opening packs';
    case 'draft':
      if (data?.phase === 'picking') return data?.setName ? `Drafting ${data.setName}` : 'Drafting';
      return 'Building a draft deck';
    default: return 'Online';
  }
}

let lastBroadcastLabel = null;
let pendingActivityTimer = null;
let pendingActivityLabel = null;

// Push activity changes to the server so friends see them. Coalesce
// rapid consecutive transitions (e.g., hub → deck-select → matchmaking
// in <1s when accepting an invite) so we don't burn one round-trip per
// screen. Only the final label in a 250 ms window actually ships.
function pushActivityLabel(label) {
  if (label === lastBroadcastLabel) return;
  pendingActivityLabel = label;
  if (pendingActivityTimer) return;
  pendingActivityTimer = setTimeout(() => {
    pendingActivityTimer = null;
    const next = pendingActivityLabel;
    if (next === lastBroadcastLabel) return;
    lastBroadcastLabel = next;
    setActivityLabel(next).catch((err) => {
      console.error('[presence] failed to push activity label:', err);
      // Allow a retry on the next transition.
      lastBroadcastLabel = null;
    });
  }, 250);
}

export function updateActivity(activity, data) {
  currentActivity = activity;
  currentActivityData = data || null;

  updateDiscordPresence(activity, data);
  pushActivityLabel(deriveStatusLabel(activity, data));
}

function updateDiscordPresence(activity, data) {
  import('../utils/arena/discordSettings.js').then(({ getDiscordSettings }) => {
    const settings = getDiscordSettings();
    if (!settings.showActivity) {
      import('./discordPresence.js').then((dp) => dp.clearPresence()).catch(() => {});
      return;
    }
    // Merge the spectator preference into match data
    if (activity === 'in-match' && data) {
      data._allowSpectators = settings.allowSpectators;
    }
    _sendDiscordPresence(activity, data);
  }).catch(() => {});
}

function _sendDiscordPresence(activity, data) {
  import('./discordPresence.js').then((dp) => {
    switch (activity) {
      case 'hub':
        dp.setPresenceHub();
        break;
      case 'store':
        dp.setPresenceStore();
        break;
      case 'deckbuilder':
      case 'deck-select':
        dp.setPresenceDeckBuilder();
        break;
      case 'matchmaking':
        dp.setPresenceMatchmaking();
        break;
      case 'in-match':
        dp.setPresenceMatch(data || {});
        break;
      case 'auction-house':
        dp.setPresenceAuctionHouse();
        break;
      case 'draft':
        dp.setPresenceDraft(data || {});
        break;
      default:
        dp.setPresenceHub();
    }
  }).catch(() => {});
}

export function stopPresence() {
  for (const unsub of unsubscribers) {
    try { unsub(); } catch {}
  }
  unsubscribers = [];
  // Reset the activity-broadcast diff so that re-login within the same
  // JS session always re-emits the first activity (otherwise the diff
  // check would suppress it and friends would see the previous user's
  // stale label until the second transition).
  lastBroadcastLabel = null;
  pendingActivityLabel = null;
  if (pendingActivityTimer) {
    clearTimeout(pendingActivityTimer);
    pendingActivityTimer = null;
  }
  if (friendRefreshTimer) {
    clearInterval(friendRefreshTimer);
    friendRefreshTimer = null;
  }
  if (visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  if (focusHandler && typeof window !== 'undefined') {
    window.removeEventListener('focus', focusHandler);
    focusHandler = null;
  }
  onFriendListUpdate = null;
  onNewNotifications = null;
  onMailCountUpdate = null;
  onChatMessage = null;
  onChatClaimed = null;
  onSessionActive = null;
  onDraftActive = null;
  onProfileUpdate = null;
  onSeasonRefreshNeeded = null;
  disconnectWebSocket();
}
